import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Play, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApi, useLoad, errMsg } from "@/lib/api";
import { PageHead, card, inputCls, Empty } from "@/components/admin/kit";

const PURPOSES = ["BP", "Sugar", "Thyroid", "Heart", "Calcium", "Cholesterol", "Antibiotic", "Pain", "Fever", "Gas", "Vitamin", "Other"];
const FORMS = ["tablet", "capsule", "syrup", "drops", "injection", "inhaler", "ointment"];
const BLANK = { written: "", purpose: "Sugar", form: "tablet", name_voice: "", active: true };

function usePlay() {
  const api = useApi();
  const [busy, setBusy] = useState("");
  const play = async (key, text) => {
    setBusy(key);
    try { const r = await api.post("/voice/test", { text, language: "te-IN" }, { responseType: "blob" }); new Audio(URL.createObjectURL(r.data)).play(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return [busy, play];
}

function MedForm({ init, onClose, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(init);
  const [busy, play] = usePlay();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try {
      const body = { written: f.written, purpose: f.purpose, form: f.form, name_voice: f.name_voice, active: f.active };
      await (f.id ? api.put(`/medicine-list/${f.id}`, body) : api.post("/medicine-list", body));
      toast.success(`${f.written} saved`); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="med-form">
        <DialogHeader><DialogTitle>{f.id ? `Edit ${init.written}` : "Add medicine"}</DialogTitle></DialogHeader>
        <label className="grid gap-1 text-sm font-semibold">Written name (as on prescriptions)<input className={inputCls} value={f.written} onChange={set("written")} maxLength={60} data-testid="med-written-input" /></label>
        <div className="grid grid-cols-2 gap-2">
          <label className="grid gap-1 text-sm font-semibold">Purpose<select className={inputCls} value={f.purpose} onChange={set("purpose")} data-testid="med-purpose-select">{PURPOSES.map((p) => <option key={p}>{p}</option>)}</select></label>
          <label className="grid gap-1 text-sm font-semibold">Form<select className={inputCls} value={f.form} onChange={set("form")} data-testid="med-form-select">{FORMS.map((p) => <option key={p}>{p}</option>)}</select></label>
        </div>
        <label className="grid gap-1 text-sm font-semibold">Telugu spoken name
          <div className="flex gap-2"><input className={`${inputCls} font-te`} value={f.name_voice} onChange={set("name_voice")} maxLength={120} data-testid="med-voice-input" />
            <Button type="button" variant="outline" disabled={!f.name_voice.trim() || !!busy} onClick={() => play("form", f.name_voice)} aria-label="Play spoken name" data-testid="med-voice-play">{busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}</Button></div>
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.active} onChange={set("active")} data-testid="med-active-checkbox" /> On (used for purpose and spoken name)</label>
        <Button onClick={save} disabled={!f.written.trim() || !f.name_voice.trim()} data-testid="med-save-btn">Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function Row({ m, onEdit, onToggle, busy, play }) {
  return (
    <tr className={`border-b border-[var(--lav-2)] ${m.active ? "" : "text-muted-foreground"}`} data-testid={`med-row-${m.key}`}>
      <td className="py-2 pr-2 font-bold">{m.written}</td>
      <td className="pr-2">{m.purpose}</td>
      <td className="pr-2">{m.form}</td>
      <td className="pr-2 font-te">{m.name_voice} <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => play(m.id, m.name_voice)} aria-label={`Play ${m.written}`} data-testid={`med-play-${m.key}`}>{busy === m.id ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}</Button></td>
      <td className="whitespace-nowrap">
        <Button size="sm" variant="outline" onClick={() => onToggle(m)} data-testid={`med-toggle-${m.key}`}>{m.active ? "Switch off" : "Switch on"}</Button>
        <Button size="sm" variant="ghost" onClick={() => onEdit(m)} aria-label="Edit" data-testid={`med-edit-${m.key}`}><Pencil size={14} /></Button>
      </td>
    </tr>
  );
}

export default function MedicineList() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [list, reload] = useLoad(`/medicine-list?q=${encodeURIComponent(q)}`);
  const [edit, setEdit] = useState(null);
  const [busy, play] = usePlay();
  const toggle = async (m) => {
    try { await api.put(`/medicine-list/${m.id}`, { written: m.written, purpose: m.purpose, form: m.form, name_voice: m.name_voice, active: !m.active }); toast.success(`${m.written} switched ${m.active ? "off" : "on"}`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="medicine-list-page">
      <PageHead title="Medicine list" sub="Purpose and Telugu spoken name come from this list, never from the AI's guess. Matching uses the first word of the medicine name (e.g. 'Glycomet GP 2' → Glycomet). Switched-off medicines are ignored." />
      <div className={`${card} grid gap-3`}>
        <div className="flex flex-wrap gap-2">
          <input className={`${inputCls} max-w-sm`} placeholder="Search name, purpose or Telugu" value={q} onChange={(e) => setQ(e.target.value)} data-testid="med-search" />
          <Button className="ml-auto" onClick={() => setEdit(BLANK)} data-testid="med-add-btn"><Plus size={16} /> Add medicine</Button>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="med-count">{list ? `${list.length} medicines` : ""}</p>
        <div className="overflow-x-auto">
          {!list ? <Empty>Loading…</Empty> : !list.length ? <Empty>No medicines match.</Empty> : (
            <table className="w-full min-w-[640px] text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground"><th>Written name</th><th>Purpose</th><th>Form</th><th>Telugu spoken name</th><th /></tr></thead>
              <tbody>{list.map((m) => <Row key={m.id} m={m} onEdit={setEdit} onToggle={toggle} busy={busy} play={play} />)}</tbody>
            </table>
          )}
        </div>
      </div>
      {edit && <MedForm init={edit} onClose={() => setEdit(null)} onSaved={reload} />}
    </div>
  );
}
